import { PartyUpsertDialog } from "@/components/parties/PartyUpsertDialog";
import { SupplierFormFields } from "@/components/suppliers/SupplierFormFields";
import { Button } from "@/components/ui/button";
import {
  createSupplier,
  emptySupplierDraft,
  isSupplierDraftComplete,
  normalizeSupplierDraft,
  supplierErrorMessage,
  validateSupplierDraft,
  type SupplierDraft,
  type SupplierRecord,
} from "@/lib/suppliers/suppliersStore";
import { Loader2, Truck } from "lucide-react";
import { useEffect, useState } from "react";
import { toast } from "sonner";

type Props = {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onCreated?: (record: SupplierRecord) => void | Promise<void>;
  title?: string;
};

export function CreateSupplierDialog({
  open,
  onOpenChange,
  onCreated,
  title = "Add supplier",
}: Props) {
  const [draft, setDraft] = useState<SupplierDraft>(() => emptySupplierDraft());
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!open) return;
    setDraft(emptySupplierDraft());
    setSaving(false);
  }, [open]);

  const save = async () => {
    if (saving) return;
    const validation = validateSupplierDraft(draft);
    if (!validation.valid) {
      toast.error(validation.message ?? "Complete all required supplier fields.");
      return;
    }
    const payload = normalizeSupplierDraft(draft);
    setSaving(true);
    try {
      const { record, message } = await createSupplier(payload);
      toast.success(message);
      onOpenChange(false);
      await onCreated?.(record);
    } catch (error) {
      toast.error(supplierErrorMessage(error));
    } finally {
      setSaving(false);
    }
  };

  return (
    <PartyUpsertDialog
      open={open}
      onOpenChange={onOpenChange}
      title={title}
      description="Add a supplier to your directory with contact and address details."
      icon={Truck}
      accent="amber"
      footer={
        <>
          <Button type="button" variant="outline" onClick={() => onOpenChange(false)} disabled={saving}>
            Cancel
          </Button>
          <Button
            type="button"
            onClick={() => void save()}
            disabled={saving || !isSupplierDraftComplete(draft)}
            className="gap-2 bg-primary hover:bg-primary/90 text-primary-foreground font-semibold px-5"
          >
            {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : null}
            Save supplier
          </Button>
        </>
      }
    >
      <SupplierFormFields draft={draft} onChange={setDraft} disabled={saving} />
    </PartyUpsertDialog>
  );
}
